import { createStore, createTypedHooks, persist } from 'easy-peasy';
import { Platform } from 'react-native';
import localActions, { LocalActions } from './actions/localActions';

import {
  GlobalPopupStateModel,
  globalPopupState,
} from './state/globalPopupState';
import globalState, { GlobalStateModel } from './state/globalState';
import selfTrainingSelection, {
  SelfTrainingModel,
} from './state/selfTrainingSelection';
import sentenceExerciseState, {
  SentenceExerciseStateModel,
} from './state/sentenceExerciseState';
import textExerciseState, {
  TextExerciseModel,
} from './state/textExerciseState';
import wordExerciseState, {
  WordExerciseStateModel,
} from './state/wordExerciseState';

import { isPatientApp } from '@/config/constants';
import authState, { AuthStateModel } from './auth/authState';
import createStorage from './encryptedStorage';
import { ErrorsModel, errors } from './error';
import { ExerciseStateModel, exerciseStateModel } from './state/exercisesState';
import { Patient } from './store.types';

/* Removing the local storage item. */
// configureStorage().removeItem('[EasyPeasyStore][0]');

// fix for iOS not calling setItem (see https://github.com/ctrlplusb/easy-peasy/issues/599#issuecomment-781258630)
// might not be needed anymore with newer RN Version
if (Platform.OS === 'ios') {
  window.requestIdleCallback = null;
}

interface BasicStoreModel {
  storedSessions: unknown;
  storedStars:
    | {
        [x: string]: { store: Record<string, number> };
      }
    | object;
  appStartResumeLock: boolean;
  eigentrainingLock: boolean;
  therapistWebAppLogin: boolean;
  refresh: boolean;
  refreshTogglerState: boolean;
  appLockPattern: string;
  showLockPatternScreen: boolean;
  lastLoginEmail: string;
  toolTipNumber: number;
  focusOnLaden: false;
  activePatient?: Patient;
  serverActions: any;
  downloadPatients: Patient[];
  patientActiveSet: string;
  patient: Patient | null;
  hasSeenNewTemplateModal: boolean;
  newNavAnimationCountButton: number;
  newNavAnimationCountNav: number;
  lastLoggedInTimestamp: Date | null;
  lastIdleDate: Date | null;
}

export type StoreModel = BasicStoreModel &
  GlobalStateModel &
  SelfTrainingModel &
  GlobalPopupStateModel &
  AuthStateModel &
  TextExerciseModel &
  SentenceExerciseStateModel &
  WordExerciseStateModel &
  ErrorsModel &
  ExerciseStateModel;

type StoreActions = LocalActions;
type Store = StoreActions & StoreModel;

const initPersistedStates = (): (keyof StoreModel)[] => {
  const persistedStates: (keyof StoreModel)[] = [
    'sets',
    'lastSyncedSets',
    'assetsLinks',
    'activePatient',
    'practiceSessionsWithKey',
    'resultsWithKey',
    'starContainer',
    'stayLoggedIn',
    'signedInUser',
    'isAuthenticated',
    'resultModalWasShown',
    'accountWasDeleted',
    'downloadPatients',
    'appStartResumeLock',
    'eigentrainingLock',
    'appLockPattern',
    'subscriptions',
    'permissions',
    'therapistWebAppLogin',
    'showLockPatternScreen',
    'lastLoginEmail',
    'seenUpdates',
    'latestUpdates',
    'patientActiveSet',
    'hasSeenNewTemplateModal',
    'newNavAnimationCountButton',
    'newNavAnimationCountNav',
    'lastLoggedInTimestamp',
    'lastIdleDate',
  ];
  if (!isPatientApp) return persistedStates;
  persistedStates.push('selfTrainingSelection');
  return persistedStates;
};

let store = createStore<Store>(
  persist<Store>(
    {
      ...localActions,
      ...errors,
      ...exerciseStateModel,
      ...wordExerciseState,
      ...sentenceExerciseState,
      ...textExerciseState,
      ...authState,
      ...globalPopupState,
      ...selfTrainingSelection,
      ...globalState,
      downloadPatients: [],
      serverActions: {},
      storedSessions: {},
      storedStars: {},
      appStartResumeLock: false,
      eigentrainingLock: false,
      therapistWebAppLogin: false,
      refresh: false,
      refreshTogglerState: false,
      appLockPattern: '',
      showLockPatternScreen: false,
      lastLoginEmail: '',
      toolTipNumber: 1,
      focusOnLaden: false,
      activePatient: null, // ? This state is responsible to storing the activePatient for patientApp
      patientActiveSet: '',
      patient: null,
      lastLoggedInTimestamp: null,
      lastIdleDate: null,
    },
    {
      storage: createStorage(),
      allow: initPersistedStates(),
    },
  ),
  {
    name: 'encryptedStorage',
  },
);

export default store;

const typedHooks = createTypedHooks<Store>();

export const useStoreActions = typedHooks.useStoreActions;
export const useStoreState = typedHooks.useStoreState;
